import React, { useState } from "react";
import { CicdFaqs } from "../../../components/faqData.js";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const CloudDeployments = () => {
  const [actIndex, setActIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActIndex(index === actIndex ? null : index);
  };
  const scrollToContact = () => {
    document
      .getElementById("contact-section")
      .scrollIntoView({ behavior: "smooth" });
  };

  const kubernetesOfferings = [
    {
      logo_images:"../assets/icons/azure-solution/EKs.png",
      title: "Amazon EKS",
      description:
        "Deploy and manage Kubernetes clusters on AWS with Amazon Elastic Kubernetes Service (EKS), providing fully managed control plane, seamless integration with AWS services, and enterprise-grade security.",
    },
    {
      logo_images:"../assets/icons/azure-solution/service-7.png",
      title: "Azure AKS",
      description:
        "Orchestrate containerized applications with Azure Kubernetes Service (AKS), offering simplified cluster management, integrated CI/CD pipelines, and native Azure Active Directory integration.",
    },
    {
      logo_images:"../assets/icons/azure-solution/gke.png",
      title: "Google GKE",
      description:
        "Leverage Google Kubernetes Engine (GKE) for fully managed Kubernetes clusters, featuring advanced autoscaling, multi-cluster management, and Google Cloud's global infrastructure.",
    },
  ];

  const architectureFeatures = [
    {
      title: "High Availability",
      description:
        "Multi-AZ deployments with automatic failover and load balancing across availability zones for 99.99% uptime.",
    },
    {
      title: "Auto-Scaling",
      description:
        "Horizontal and vertical pod autoscaling based on CPU, memory, and custom metrics to optimize resource utilization.",
    },
    {
      title: "Service Mesh",
      description:
        "Istio and Linkerd integration for advanced traffic management, security policies, and observability.",
    },
    {
      title: "Container Registry",
      description:
        "Integrated container registries (ECR, ACR, GCR) with automated image scanning and vulnerability management.",
    },
  ];

  const cicdFeatures = [
    {
      title: "Automated Build & Deploy",
      description:
        "CI/CD pipelines with Jenkins, GitLab CI, GitHub Actions, or cloud-native tools (CodePipeline, Azure DevOps, Cloud Build) for automated container builds and deployments.",
    },
    {
      title: "GitOps Workflows",
      description:
        "ArgoCD and Flux integration for declarative GitOps deployments, enabling version-controlled infrastructure and application updates.",
    },
    {
      title: "Blue-Green & Canary",
      description:
        "Advanced deployment strategies with zero-downtime rollouts, automated rollback capabilities, and progressive traffic shifting.",
    },
    {
      title: "Multi-Environment",
      description:
        "Automated promotion pipelines across development, staging, and production environments with environment-specific configurations.",
    },
  ];

  const scalabilityFeatures = [
    "Horizontal pod autoscaling based on CPU, memory, and custom metrics",
    "Cluster autoscaling to dynamically add/remove nodes based on workload",
    "Network policies for fine-grained traffic control and micro-segmentation",
    "Ingress controllers (NGINX, Traefik) for external traffic management",
    "Service mesh for advanced networking, security, and observability",
    "Secrets management with cloud-native key vaults (AWS Secrets Manager, Azure Key Vault, GCP Secret Manager)",
  ];

  const tools = [
    "kubectl",
    "Helm",
    "ArgoCD",
    "Terraform",
    "Ansible",
    "Istio",
    "Prometheus",
    "Grafana",
    "Fluentd",
    "ELK Stack",
    "Jenkins",
    "GitLab CI",
  ];

  const useCases = [
    {
      title: "Microservices Architecture",
      description:
        "Deployed a containerized microservices application on EKS with 50+ services, achieving 99.99% availability, auto-scaling from 10 to 500 pods, and reducing deployment time by 80%.",
    },
    {
      title: "Multi-Cloud Kubernetes",
      description:
        "Implemented a hybrid Kubernetes strategy using AKS and GKE across regions, enabling disaster recovery, workload distribution, and compliance with data residency requirements.",
    },
    {
      title: "CI/CD Pipeline Automation",
      description:
        "Built end-to-end GitOps workflows with ArgoCD on GKE, automating deployments across 5 environments and reducing manual intervention by 95% while ensuring rollback capabilities.",
    },
    {
      title: "Legacy Application Modernization",
      description:
        "Containerized and migrated legacy monolithic applications to AKS, improving resource utilization by 60%, enabling zero-downtime deployments, and reducing infrastructure costs by 40%.",
    },
  ];

  const whyChooseUs = [
    "Certified Kubernetes Administrators (CKA) and Application Developers (CKAD)",
    "Multi-cloud expertise across EKS, AKS, and GKE platforms",
    "End-to-end services from architecture design to 24/7 managed operations",
    "Cost optimization strategies reducing Kubernetes spend by 30-50%",
    "Security hardening with network policies, RBAC, and pod security standards",
    "Automated monitoring and alerting with Prometheus, Grafana, and cloud-native tools",
  ];

  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Accelerate your business with expert cloud deployment services. We ensure seamless migration, automated workflows, and maximum security for your infrastructure. Get a quote today."
        />
        <meta name="copyright" content="codeglim" />
        <meta
          property="og:title"
          content="Professional Cloud Deployments Services | Secure & Scalable Solutions | Ayanshtech Solutions"
        />
        <meta property="og:site_name" content="Ayanshtech Solutions" />
        <meta
          property="og:url"
          content="http://ayanshtechnology.com/devops-cloud-native"
        />
        <meta
          property="og:description"
          content="Accelerate your business with expert cloud deployment services. We ensure seamless migration, automated workflows, and maximum security for your infrastructure. Get a quote today."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="Professional Cloud Deployments Services | Secure & Scalable Solutions | Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Accelerate your business with expert cloud deployment services. We ensure seamless migration, automated workflows, and maximum security for your infrastructure. Get a quote today."
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <link
          rel="canonical"
          href="http://ayanshtechnology.com/devops-cloud-native"
        />
        <title>
          Professional Cloud Deployments Services | Secure & Scalable Solutions | Ayanshtech Solutions
        </title>
      </Helmet>
      <div className="flex flex-col items-center justify-center mb-6 p-6">
        {/* Hero Section */}
        <div className="py-5 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-3/4 lg:text-left p-4">
              <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                Cloud Deployments (EKS | AKS | GKE)
              </h1>

              <p className="sm:text-base md:text-lg lg:text-lg mb-3">
                Deploy and manage containerized applications at scale with
                managed Kubernetes services from leading cloud providers.
                Ayanshtech Solutions delivers expert cloud deployment services
                for Amazon EKS, Azure AKS, and Google GKE, enabling you to
                leverage the full power of Kubernetes without the operational
                overhead.
              </p>

              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Our certified Kubernetes engineers design, deploy, and optimize
                cloud-native architectures that scale automatically, ensure high
                availability, and integrate seamlessly with your CI/CD
                pipelines. From cluster setup and configuration to ongoing
                management and optimization, we help you build resilient,
                secure, and cost-effective container orchestration platforms.
              </p>

              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Whether you're migrating to Kubernetes, building
                cloud-native applications, or optimizing existing clusters, our
                team ensures best practices, security compliance, and
                performance optimization at every step of your Kubernetes
                journey.
              </p>

              <button
                onClick={scrollToContact}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
              >
                Get Started
              </button>
            </div>

            <div className="flex w-full md:w-1/2 justify-center mb-12 lg:mb-0">
              <img
                src="../assets/images/devops-pages/Cloud-Deploy-banner.webp"
                alt="Cloud Kubernetes Deployments"
                className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* Introduction to Cloud Kubernetes Deployments */}
        {/* <div className="mb-5 mt-10 p-4">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Introduction to Cloud Kubernetes Deployments
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Kubernetes has become the de facto standard for container
            orchestration, enabling organizations to deploy, scale, and manage
            containerized applications efficiently. Managed Kubernetes services
            from AWS (EKS), Azure (AKS), and Google (GKE) eliminate the
            complexity of managing control planes while providing enterprise-grade
            features, security, and integration with cloud-native services.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            At Ayanshtech, we specialize in designing and implementing Kubernetes
            deployments across all major cloud platforms. Our comprehensive
            services cover cluster architecture, CI/CD integration, networking,
            security, monitoring, and ongoing optimization to ensure your
            containerized workloads run efficiently and reliably in production.
          </p>
        </div> */}

        {/* Managed Kubernetes Offerings */}
        <div className="text-center mb-5 mt-12  ">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Managed Kubernetes Offerings
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Choose from leading managed Kubernetes platforms, each offering unique
            advantages and seamless integration with their respective cloud
            ecosystems.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {kubernetesOfferings.map((offering, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"

                >
                   <div className="flex items-center gap-2">
                    <div className="w-[60px] h-[60px]  ">
                      <img src={offering.logo_images} className="w-full rounded-2xl" alt="" />
                    </div>
                  <h3 className="text-lg font-bold">{offering.title}</h3>
                  </div>
                  <p className="text-lg">{offering.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Architecture Overview */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Architecture Overview
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Our Kubernetes architectures are designed for high availability,
            scalability, and security, following cloud-native best practices and
            industry standards.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8">
              {architectureFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <h3 className="text-lg font-bold">{feature.title}</h3>
                  <p className="text-lg">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Deployment Automation (CI/CD) */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Deployment Automation (CI/CD)
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Automate your Kubernetes deployments with integrated CI/CD pipelines,
            GitOps workflows, and advanced deployment strategies for reliable,
            zero-downtime releases.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8">
              {cicdFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <h3 className="text-lg font-bold">{feature.title}</h3>
                  <p className="text-lg">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scalability, Networking & Security */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Scalability, Networking & Security
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Build resilient, secure, and scalable Kubernetes deployments with
            advanced networking, autoscaling, and security features.
          </p>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {scalabilityFeatures.map((feature, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none "
              >
                <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{feature}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tools We Use */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Tools We Use
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Our Kubernetes deployments leverage industry-leading tools and
            platforms for cluster management, CI/CD automation, monitoring, and
            infrastructure as code.
          </p>
        </div>

        <div className="p-4">
          <div className="flex flex-wrap justify-center gap-4">
            {tools.map((tool, index) => (
            
              <span
                key={index}
                className=" group
        relative z-0
        px-4 py-2 rounded-full font-semibold
        bg-red-50 text-blue-700 border border-blue-100
        overflow-hidden cursor-pointer transition-all
        
        before:absolute before:inset-0
        before:bg-gradient-to-r before:from-blue-500 before:to-green-500
        before:translate-x-[-101%]
        before:transition-transform before:duration-300
        hover:before:translate-x-0"
              >
                <span  className="relative z-10 group-hover:text-white">
                {tool}
              </span>
              </span>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Real-World Use Cases
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            See how we've helped organizations achieve their cloud-native
            transformation goals with Kubernetes deployments.
          </p>
        </div>

        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <h3 className="text-lg font-bold">{useCase.title}</h3>
                  <p className="text-lg">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Our Deployment Services */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Why Choose Our Deployment Services
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Partner with Ayanshtech for Kubernetes expertise that drives
              measurable business outcomes. Our proven methodology, certified
              engineers, and customer-first approach ensure successful
              cloud-native transformations.
            </p>
          </div>

          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                >
                  <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                  <p className="text-lg text-gray-700">{reason}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={scrollToContact}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
            >
              Schedule a Free Kubernetes Consultation Today!
            </button>
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto p-6 py-20 w-full">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-8">
            FAQs
          </h2>
          {CicdFaqs.map((faq, index) => (
            <div key={index} className="border-b">
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full text-left py-4 flex justify-between items-center focus:outline-none ${
                  actIndex === index
                    ? "bg-blue-600 text-white px-5 rounded-2xl"
                    : ""
                }`}
              >
                <span className="text-lg">{faq.question}</span>
                <span className="text-2xl">
                  {actIndex === index ? "-" : "+"}
                </span>
              </button>
              {actIndex === index && (
                <div className="p-4 text-gray-700">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div id="contact-section">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default CloudDeployments;

